/* eslint-disable react/no-array-index-key */
import React, { useMemo, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { path, applySpec, propOr, props } from 'ramda';

import { useAsyncState } from 'utils/useAsyncState';
import {
  useReportsActions,
  useAlerts,
  SUB_PATIENT,
  ID,
  RECEIVER,
  RECEIVER_TYPE,
  VALIDATION_ISSUES,
  REPORT_FILE,
  REPORT_URL,
} from 'store/reports';
import {
  IS_EMERGENCY,
  TITLE,
  FIRST_NAME,
  LAST_NAME,
  GENDER,
  INSTITUTION,
  CARE_OF,
  ADDRESS,
  ZIP_CODE,
  CITY,
  CANTON,
  COUNTRY,
  EMAIL,
  STATUS,
} from 'store/patients';
import FullScreenSpinner from 'components/FullScreenSpinner';
import Accordion from 'components/Accordion';
import Confirm from 'components/Confirm';

import Panel from './Panel';
import PdfSection from './PdfSection';
import ReceiverFrom from './ReceiverFrom';
import ContactPanel from './ContactPanel';
import RecreateButton from './RecreateButton';
import { Container, NavLink, WarnBlock, Message, Title } from './styles';

const Report = () => {
  const { id } = useParams();
  const [data, setData] = useAsyncState(null);
  const { fetchReport, saveReportDebounce } = useReportsActions();
  const { action, loading, success } = useAlerts(fetchReport);
  const { action: save, errors: saveErrors, success: updatedReport } = useAlerts(saveReportDebounce);
  const [patientId, initialValues] = useMemo(
    () => [
      path([SUB_PATIENT, ID], data),
      applySpec({
        [IS_EMERGENCY]: propOr(false, [IS_EMERGENCY]),
        [TITLE]: propOr('', [TITLE]),
        [FIRST_NAME]: propOr('', [FIRST_NAME]),
        [LAST_NAME]: propOr('', [LAST_NAME]),
        [GENDER]: propOr('', [GENDER]),
        [INSTITUTION]: propOr('', [INSTITUTION]),
        [CARE_OF]: propOr('', [CARE_OF]),
        [ADDRESS]: propOr('', [ADDRESS]),
        [ZIP_CODE]: propOr('', [ZIP_CODE]),
        [CITY]: propOr('', [CITY]),
        [CANTON]: propOr('', [CANTON]),
        [COUNTRY]: propOr('', [COUNTRY]),
        [EMAIL]: propOr('', [EMAIL]),
      })(data?.[RECEIVER] || {}),
    ],
    [data]
  );
  const [status, validationIssues] = props([STATUS, VALIDATION_ISSUES], data);
  const isEditable = [0, 1, 3, 4, 6, 7].includes(status);
  const onSave = useCallback((values) => save({ [ID]: id, [RECEIVER]: values, [STATUS]: status }), [id, save, status]);
  const withdrawnReport = useCallback(() => save({ [ID]: id, [STATUS]: 8 }), [id, save]);

  useEffect(() => {
    action(id);
  }, [action, id]);

  useEffect(() => {
    if (success || updatedReport) setData(updatedReport || success);
  }, [setData, success, updatedReport]);

  if (!data || loading) return <FullScreenSpinner />;

  return (
    <Container>
      <Panel report={data} />
      <NavLink to={`/patients/${patientId}`}>Zum Patientendossier</NavLink>
      <PdfSection fileData={data[REPORT_FILE]} url={data[REPORT_URL]} />
      {status === 7 && validationIssues.length > 0 && (
        <Accordion label="Fehlermeldung" defaultOpen>
          <WarnBlock>
            {validationIssues.map((message, idx) => (
              <div key={idx}>
                <Message>{message}</Message>
              </div>
            ))}
          </WarnBlock>
        </Accordion>
      )}
      <Formik initialValues={initialValues} validate={onSave} validateOnBlur={false} enableReinitialize>
        <Accordion label="Empfänger" defaultOpen>
          {isEditable && <ContactPanel type={data[RECEIVER_TYPE]} patient={data[SUB_PATIENT]} />}
          <ReceiverFrom type={data[RECEIVER_TYPE]} errors={saveErrors?.[RECEIVER]} readMode={!isEditable} />
        </Accordion>
      </Formik>
      <RecreateButton id={id} setData={setData} disabled={status === 6} />
      {isEditable && (
        <>
          <Title>Versand abbrechen</Title>
          <Confirm title="Zurückrufen" onClick={withdrawnReport} />
        </>
      )}
    </Container>
  );
};

export default Report;
